import Raven from 'raven-js';
import { mod, getCurrentFiscalYear, getQuarterForMonth, getFormattedMonth, getFormattedQuarterRange } from 'ui-deal-management-components-lib/utils/dateUtils';
import { formatDate } from './dateRangeUtils';
import { CUSTOM_DATE_OPTION, TIME_PERIOD_FILTERS } from '../constants/FiltersConstants';
import { MONTH, QUARTER } from '../constants/PipelineCadenceTypes';
export const ONE_DAY_IN_MS = 86400000;
export const INITIAL_QUARTER = 1;
export const FINAL_QUARTER = 4;
export const INITIAL_MONTH = 1;
export const FINAL_MONTH = 12;
export const MONTHS_IN_YEAR = 12;
export const QUARTERS_IN_YEAR = 4;
export const getCurrentMonth = currentDate => {
  return currentDate.getMonth() + 1;
};
export const getCurrentYear = currentDate => {
  return currentDate.getFullYear();
};
export const getNextYear = currentDate => {
  return getCurrentYear(currentDate) + 1;
};
export const getLastYear = currentDate => {
  return getCurrentYear(currentDate) - 1;
};
export const getCurrentQuarter = currentDate => {
  return getQuarterForMonth({
    month: getCurrentMonth(currentDate)
  });
};
export const getLastMonth = currentDate => {
  const currentMonth = getCurrentMonth(currentDate);
  return currentMonth === INITIAL_MONTH ? FINAL_MONTH : currentMonth - 1;
};
export const getNextMonth = currentDate => {
  const currentMonth = getCurrentMonth(currentDate);
  return currentMonth === FINAL_MONTH ? INITIAL_MONTH : currentMonth + 1;
};
export const getQuarterFromDate = date => Math.floor(date.getMonth() / 3 + 1);
export const deriveCurrentTimePeriodForCadence = ({
  cadence,
  currentMonth,
  fiscalOffset = 0
}) => {
  const currentFiscalQuarter = getQuarterForMonth({
    month: currentMonth,
    offset: fiscalOffset
  });
  return cadence === QUARTER ? currentFiscalQuarter : currentMonth;
};
const isTimePeriodCustomHelper = value => {
  return value && typeof value.get === 'function' && value.get('startDate') !== undefined;
};
export const isTimePeriodCustom = timePeriod => {
  return isTimePeriodCustomHelper(timePeriod);
};
export const getFormattedYearRange = ({
  fiscalYear,
  offset
}) => {
  const firstMonth = getFormattedMonth({
    month: offset + 1 || INITIAL_MONTH,
    // null because we don't need to show year twice if it's the same
    // for first and final months
    year: offset ? fiscalYear - 1 : null
  });
  const finalMonth = getFormattedMonth({
    month: offset ? mod(offset, MONTHS_IN_YEAR) : FINAL_MONTH,
    year: fiscalYear
  });
  return `${firstMonth} - ${finalMonth}`;
};
export const getFormattedDateRange = ({
  currentDate,
  timePeriod,
  offset = 0
}) => {
  const currentMonth = getCurrentMonth(currentDate);
  const currentYear = getCurrentYear(currentDate);
  const lastMonth = getLastMonth(currentDate);
  const nextMonth = getNextMonth(currentDate);
  const currentFiscalYear = getCurrentFiscalYear({
    currentMonth,
    offset,
    currentYear
  });
  const currentFiscalQuarter = getQuarterForMonth({
    month: currentMonth,
    offset
  });
  const lastFiscalQuarter = currentFiscalQuarter === INITIAL_QUARTER ? FINAL_QUARTER : currentFiscalQuarter - 1;
  const nextFiscalQuarter = currentFiscalQuarter === FINAL_QUARTER ? INITIAL_QUARTER : currentFiscalQuarter + 1;
  const lastQuarterFiscalYear = lastFiscalQuarter === FINAL_QUARTER ? currentFiscalYear - 1 : currentFiscalYear;
  const nextQuarterFiscalYear = nextFiscalQuarter === INITIAL_QUARTER ? currentFiscalYear + 1 : currentFiscalYear;
  const lastMonthYear = lastMonth === FINAL_MONTH ? currentYear - 1 : currentYear;
  const nextMonthYear = nextMonth === INITIAL_MONTH ? currentYear + 1 : currentYear;
  if (isTimePeriodCustom(timePeriod)) {
    const formattedStart = formatDate(timePeriod.get('startDate'));
    const formattedEnd = formatDate(timePeriod.get('endDate'));
    return `${formattedStart} - ${formattedEnd}`;
  }
  switch (timePeriod) {
    case TIME_PERIOD_FILTERS.LAST_MONTH:
      return getFormattedMonth({
        month: lastMonth,
        year: lastMonthYear
      });
    case TIME_PERIOD_FILTERS.THIS_MONTH:
      return getFormattedMonth({
        month: currentMonth,
        year: currentYear
      });
    case TIME_PERIOD_FILTERS.NEXT_MONTH:
      return getFormattedMonth({
        month: nextMonth,
        year: nextMonthYear
      });
    case TIME_PERIOD_FILTERS.LAST_QUARTER:
    case TIME_PERIOD_FILTERS.LAST_FISCAL_QUARTER:
      return getFormattedQuarterRange({
        quarter: lastFiscalQuarter,
        fiscalYear: lastQuarterFiscalYear,
        offset
      });
    case TIME_PERIOD_FILTERS.THIS_QUARTER:
    case TIME_PERIOD_FILTERS.THIS_FISCAL_QUARTER:
      return getFormattedQuarterRange({
        quarter: currentFiscalQuarter,
        fiscalYear: currentFiscalYear,
        offset
      });
    case TIME_PERIOD_FILTERS.NEXT_QUARTER:
    case TIME_PERIOD_FILTERS.NEXT_FISCAL_QUARTER:
      return getFormattedQuarterRange({
        quarter: nextFiscalQuarter,
        fiscalYear: nextQuarterFiscalYear,
        offset
      });
    case TIME_PERIOD_FILTERS.LAST_YEAR:
    case TIME_PERIOD_FILTERS.LAST_FISCAL_YEAR:
      return getFormattedYearRange({
        fiscalYear: currentFiscalYear - 1,
        offset
      });
    case TIME_PERIOD_FILTERS.THIS_YEAR:
    case TIME_PERIOD_FILTERS.THIS_FISCAL_YEAR:
      return getFormattedYearRange({
        fiscalYear: currentFiscalYear,
        offset
      });
    case TIME_PERIOD_FILTERS.NEXT_YEAR:
    case TIME_PERIOD_FILTERS.NEXT_FISCAL_YEAR:
      return getFormattedYearRange({
        fiscalYear: currentFiscalYear + 1,
        offset
      });
    default:
      Raven.captureMessage('Invalid time period passed to getTimePeriodOffset', {
        extra: {
          timePeriod
        }
      });
      return getFormattedMonth({
        month: currentMonth,
        year: currentYear
      });
    // Default initial filter state is THIS_MONTH, so default to that here
  }
};
export const getTimePeriodOffset = timePeriod => {
  if (isTimePeriodCustom(timePeriod)) {
    return 0;
  }
  switch (timePeriod) {
    case TIME_PERIOD_FILTERS.LAST_MONTH:
    case TIME_PERIOD_FILTERS.LAST_QUARTER:
    case TIME_PERIOD_FILTERS.LAST_FISCAL_QUARTER:
    case TIME_PERIOD_FILTERS.LAST_YEAR:
    case TIME_PERIOD_FILTERS.LAST_FISCAL_YEAR:
      return -1;
    case TIME_PERIOD_FILTERS.THIS_MONTH:
    case TIME_PERIOD_FILTERS.THIS_QUARTER:
    case TIME_PERIOD_FILTERS.THIS_FISCAL_QUARTER:
    case TIME_PERIOD_FILTERS.THIS_YEAR:
    case TIME_PERIOD_FILTERS.THIS_FISCAL_YEAR:
      return 0;
    case TIME_PERIOD_FILTERS.NEXT_MONTH:
    case TIME_PERIOD_FILTERS.NEXT_QUARTER:
    case TIME_PERIOD_FILTERS.NEXT_FISCAL_QUARTER:
    case TIME_PERIOD_FILTERS.NEXT_YEAR:
    case TIME_PERIOD_FILTERS.NEXT_FISCAL_YEAR:
      return 1;
    default:
      Raven.captureMessage('Invalid time period passed to getTimePeriodOffset', {
        extra: {
          timePeriod
        }
      });
      return 0;
    // Default initial filter state is THIS_MONTH, so default to that here
  }
};
export const getTimePeriodOption = timePeriod => {
  if (isTimePeriodCustom(timePeriod)) {
    return CUSTOM_DATE_OPTION;
  }
  switch (timePeriod) {
    case TIME_PERIOD_FILTERS.LAST_MONTH:
    case TIME_PERIOD_FILTERS.THIS_MONTH:
    case TIME_PERIOD_FILTERS.NEXT_MONTH:
      return 'MONTH';
    case TIME_PERIOD_FILTERS.LAST_QUARTER:
    case TIME_PERIOD_FILTERS.THIS_QUARTER:
    case TIME_PERIOD_FILTERS.NEXT_QUARTER:
    case TIME_PERIOD_FILTERS.LAST_FISCAL_QUARTER:
    case TIME_PERIOD_FILTERS.THIS_FISCAL_QUARTER:
    case TIME_PERIOD_FILTERS.NEXT_FISCAL_QUARTER:
      return 'QUARTER';
    case TIME_PERIOD_FILTERS.LAST_YEAR:
    case TIME_PERIOD_FILTERS.THIS_YEAR:
    case TIME_PERIOD_FILTERS.NEXT_YEAR:
    case TIME_PERIOD_FILTERS.LAST_FISCAL_YEAR:
    case TIME_PERIOD_FILTERS.THIS_FISCAL_YEAR:
    case TIME_PERIOD_FILTERS.NEXT_FISCAL_YEAR:
      return 'YEAR';
    default:
      Raven.captureMessage('Invalid time period passed to getTimePeriodOffset', {
        extra: {
          timePeriod
        }
      });
      return 'MONTH';
    // Default initial filter state is THIS_MONTH, so default to that here
  }
};
export const getOffsetTimePeriod = ({
  cadence,
  currentTimePeriod,
  timePeriod
}) => {
  const periodsInYear = cadence === MONTH ? MONTHS_IN_YEAR : QUARTERS_IN_YEAR;
  return mod(getTimePeriodOffset(timePeriod) + currentTimePeriod, periodsInYear);
};
export const deriveSubmissionStaleness = (lastModifiedDate, staleSubmissionLimit) => {
  if (!lastModifiedDate || !staleSubmissionLimit) {
    return false;
  }
  // Compares the number of days since the last modification to the stale submission limit (in milliseconds)
  return Date.now() - lastModifiedDate >= staleSubmissionLimit * ONE_DAY_IN_MS;
};
export const submissionLimitTimestamp = staleSubmissionAmount => new Date(Date.now() - staleSubmissionAmount * ONE_DAY_IN_MS);